import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, apiError, eur, deDate } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Plus } from "lucide-react";

const EMPTY = { first_name: "", last_name: "", birth_date: "", address: "", zip: "", city: "", country: "Deutschland", phone: "", email: "", id_type: "", id_number: "", notes: "" };

export default function Sellers() {
  const [sellers, setSellers] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [detail, setDetail] = useState(null);
  const [params] = useSearchParams();

  const load = () => api.get(`/sellers?q=${encodeURIComponent(q)}`).then((r) => setSellers(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => { if (params.get("neu")) setOpen(true); }, [params]);

  const save = async () => {
    try {
      await api.post("/sellers", form);
      toast.success("Verkäufer angelegt.");
      setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const openDetail = async (id) => {
    const { data } = await api.get(`/sellers/${id}`);
    setDetail(data);
  };

  return (
    <div className="space-y-6" data-testid="sellers-page">
      <div className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-zinc-400">Ankauf</div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mt-1">Verkäufer</h1>
        </div>
        <div className="flex gap-3">
          <Input placeholder="Suchen…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} data-testid="sellers-search" className="w-56" />
          <Button onClick={() => setOpen(true)} className="bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="new-seller-open-button"><Plus className="w-4 h-4 mr-1" /> Neuer Verkäufer</Button>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-zinc-100 text-left">
            <th className="px-5 py-3 text-[10px] font-mono uppercase tracking-wider text-zinc-400">Nr.</th>
            <th className="px-5 py-3 text-[10px] font-mono uppercase tracking-wider text-zinc-400">Name</th>
            <th className="px-5 py-3 text-[10px] font-mono uppercase tracking-wider text-zinc-400">Kontakt</th>
            <th className="px-5 py-3 text-[10px] font-mono uppercase tracking-wider text-zinc-400">Ausweis</th>
            <th className="px-5 py-3 text-[10px] font-mono uppercase tracking-wider text-zinc-400">Seit</th>
          </tr></thead>
          <tbody className="divide-y divide-zinc-50">
            {sellers.length === 0 && <tr><td colSpan={5} className="px-5 py-8 text-center text-zinc-400">Noch keine Verkäufer.</td></tr>}
            {sellers.map((s) => (
              <tr key={s.id} onClick={() => openDetail(s.id)} data-testid={`seller-row-${s.number}`} className="hover:bg-zinc-50 cursor-pointer">
                <td className="px-5 py-3 font-mono text-xs">{s.number}</td>
                <td className="px-5 py-3">{s.first_name} {s.last_name}</td>
                <td className="px-5 py-3 text-zinc-500">{s.phone || s.email || "–"}</td>
                <td className="px-5 py-3 text-zinc-500 font-mono text-xs">{s.id_number ? `${s.id_type} ${s.id_number}` : "–"}</td>
                <td className="px-5 py-3 text-zinc-500">{deDate(s.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="seller-form-dialog">
          <DialogHeader><DialogTitle>Neuer Verkäufer</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            {Object.entries({ first_name: "Vorname *", last_name: "Nachname *", birth_date: "Geburtsdatum", phone: "Telefon", email: "E-Mail", address: "Adresse", zip: "PLZ", city: "Ort", country: "Land", id_type: "Ausweisart", id_number: "Ausweisnummer", notes: "Notizen" }).map(([k, l]) => (
              <div key={k} className={["address", "notes"].includes(k) ? "col-span-2" : ""}>
                <Label>{l}</Label>
                <Input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`seller-form-${k}`} className="mt-1.5" />
              </div>
            ))}
          </div>
          <Button onClick={save} disabled={!form.first_name || !form.last_name} className="w-full bg-zinc-900 hover:bg-zinc-800 text-white mt-4" data-testid="seller-save-button">Speichern</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="seller-detail-dialog">
          {detail && (
            <>
              <DialogHeader><DialogTitle>{detail.seller.first_name} {detail.seller.last_name} <span className="font-mono text-sm text-zinc-400">{detail.seller.number}</span></DialogTitle></DialogHeader>
              <div className="text-sm space-y-4">
                <div className="grid grid-cols-2 gap-3 text-zinc-600">
                  <div>Geburtsdatum: {detail.seller.birth_date || "–"}</div>
                  <div>Telefon: {detail.seller.phone || "–"}</div>
                  <div className="col-span-2">{detail.seller.address}, {detail.seller.zip} {detail.seller.city}</div>
                  {detail.seller.id_number && <div className="col-span-2 font-mono text-xs">Ausweis: {detail.seller.id_type} {detail.seller.id_number}</div>}
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 mb-2">Verkäuferhistorie · Ankäufe ({detail.purchases.length})</div>
                  {detail.purchases.length === 0 && <div className="text-zinc-400">Noch keine Ankäufe.</div>}
                  <div className="space-y-1.5">
                    {detail.purchases.map((p) => (
                      <div key={p.id} className="flex justify-between border border-zinc-100 rounded-lg px-3 py-2">
                        <span className="font-mono text-xs">{p.number}</span>
                        <span className="text-zinc-500 text-xs">{deDate(p.date)}</span>
                        <span className="font-medium">{eur(p.total)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
